import { Outlet } from "react-router-dom";
import { TERMS_VERSION } from "@school-sales-ai/shared";
import { TermsAcceptanceGate } from "@/components/TermsAcceptanceGate";
import { useCurrentUserProfileQuery } from "@/hooks/useUsers";

function FullScreenMessage({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-ink-050 px-4 text-center">
      {children}
    </div>
  );
}

/**
 * Route guard directly under `ProtectedRoute`, so it covers EVERY authenticated
 * screen — the onboarding wizard included, which lives outside `Layout`.
 *
 * Every role must accept, and must accept again whenever `TERMS_VERSION`
 * changes: the comparison is equality with the version this build displays,
 * never "has accepted something". Enforcement is CRM-side only by product
 * decision; the API does not refuse a user who has not accepted.
 *
 * An error is NOT treated as loading. This guard wraps the whole app, so a
 * failed `/crm/users/me` shown as a spinner would hang the CRM with no way out.
 */
export function TermsGate(): React.JSX.Element {
  const { data: profile, isError, refetch } = useCurrentUserProfileQuery();

  if (isError) {
    return (
      <FullScreenMessage>
        <span className="text-sm text-ink-700">No pudimos cargar tu perfil.</span>
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-sm font-semibold text-moss underline"
        >
          Reintentar
        </button>
      </FullScreenMessage>
    );
  }

  if (!profile) {
    return (
      <FullScreenMessage>
        <span className="text-sm text-ink-500">Cargando…</span>
      </FullScreenMessage>
    );
  }

  if (profile.termsAcceptedVersion !== TERMS_VERSION) {
    return <TermsAcceptanceGate />;
  }

  return <Outlet />;
}
